/**
 * Galería de fotos del inmueble: subir, marcar portada, reordenar, poner pie y borrar.
 *
 * Sustituye al rayado a 45° del maquetado, que se queda EXACTAMENTE IGUAL cuando no hay fotos:
 * una ficha recién creada tiene que seguir pareciéndose al diseño, no a un hueco roto.
 *
 * EL ORDEN SE MUEVE CON FLECHAS, y ADEMÁS arrastrando. En ese orden y no al revés: arrastrar
 * es más lucido pero deja fuera a quien navega con teclado, a quien usa un lector de pantalla
 * y a quien tiene un ratón que no perdona. Las flechas son el camino garantizado; el arrastre
 * es una comodidad encima, con la API nativa de HTML —treinta miniaturas no justifican una
 * librería de arrastre— y sin quitarle nada a nadie.
 *
 * `PUT .../photos/order` recibe la lista COMPLETA de identificadores en el orden deseado, así
 * que aquí se calcula el array entero y no un «mueve esta a la posición 3»: el servidor no
 * tiene que adivinar nada y dos reordenaciones seguidas no se pisan.
 */
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PropertyPhotoDto } from '@naar/shared';
import { ApiError, api, queryKeys } from '../../api/client';
import { Icon } from '../../components/Icon';
import { ConfirmDialog } from '../../components/overlay/Drawer';
import { Lightbox } from '../../components/overlay/Lightbox';
import { toast } from '../../components/feedback/Toaster';
import { useFlip } from '../../lib/useFlip';
import styles from './PropertyPhotos.module.css';

/** Lo que el servidor acepta por olfato de bytes; el `accept` solo filtra el diálogo del SO. */
const ACCEPT = 'image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif';

interface PropertyPhotosProps {
  propertyId: string;
  photos: PropertyPhotoDto[];
  canManage: boolean;
}

export function PropertyPhotos({ propertyId, photos, canManage }: PropertyPhotosProps) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<PropertyPhotoDto | null>(null);
  /**
   * Fotos cuyo fichero NO carga.
   *
   * La API firma la URL sin comprobar antes que el objeto siga en el almacén —hacerlo costaría
   * una petición por foto y por carga de ficha—, así que una fila cuyo objeto no está devuelve
   * una URL perfectamente válida que da 404. Es exactamente lo que pasa con las fotos de la
   * siembra, que son filas sin fichero detrás. El navegador lo descubre gratis al intentar
   * pintarla, y entonces se cae al marcador de posición en vez de dejar el icono de imagen
   * rota. Cubre también el caso de una URL caducada mientras la ficha estaba abierta.
   */
  const [broken, setBroken] = useState<Set<string>>(new Set());
  /** Índice de la foto abierta a tamaño completo, o `null` si la vista ampliada está cerrada. */
  const [viewing, setViewing] = useState<number | null>(null);
  /**
   * REORDENAR ARRASTRANDO, con las demás fotos recolocándose en vivo.
   *
   * Mientras arrastras, el orden cambia DE VERDAD en pantalla —no al soltar— y cada foto se
   * desliza a su sitio nuevo (`useFlip`). La galería grande y la tira comparten ese orden, así
   * que arrastrar en una mueve también la otra.
   *
   * `order` es el orden PROVISIONAL: el que se ve durante el arrastre y, tras soltar, hasta que
   * la API devuelve el definitivo; sin él, la foto volvería un instante a su sitio viejo antes
   * de saltar al nuevo. Su primera foto ES la portada, igual que la API pone la portada
   * delante: la foto grande y la primera de la tira son siempre la misma.
   */
  const flip = useFlip();
  const [order, setOrder] = useState<string[] | null>(null);
  /** Foto que se arrastra. Estado para pintarla apagada; ref para leerla sin esperar al render. */
  const [dragId, setDragId] = useState<string | null>(null);
  const dragIdRef = useRef<string | null>(null);
  /**
   * Tras cada recolocación se ignoran los `dragover` durante lo que dura la animación. Si no,
   * la foto que se está apartando pasa por debajo del cursor, cuenta como nuevo destino y las
   * dos se intercambian una y otra vez.
   */
  const lockUntil = useRef(0);
  const dropped = useRef(false);
  /**
   * Hay ficheros sobrevolando la galería. Es OTRO arrastre distinto del de reordenar: aquel
   * mueve una miniatura dentro de la tira y no lleva ficheros; este trae fotos desde fuera del
   * navegador. Se distinguen mirando si el `dataTransfer` trae `Files`.
   */
  const [sobreGaleria, setSobreGaleria] = useState(false);

  /**
   * La tira de miniaturas es UNA fila que se desplaza, con flechas.
   *
   * Antes era una rejilla con todas las fotos a la vista: con veinte fotos y su pie cada una,
   * la tarjeta medía dos pantallas y arrastraba consigo la tarjeta de al lado, que se
   * estiraba con un hueco vacío bajo el mapa. Las flechas solo aparecen cuando hay algo hacia
   * ese lado, para no ofrecer un desplazamiento que no lleva a ninguna parte.
   */
  const stripRef = useRef<HTMLUListElement>(null);
  const [haciaAtras, setHaciaAtras] = useState(false);
  const [haciaDelante, setHaciaDelante] = useState(false);

  function medirTira() {
    const tira = stripRef.current;
    if (!tira) return;
    setHaciaAtras(tira.scrollLeft > 4);
    setHaciaDelante(tira.scrollLeft + tira.clientWidth < tira.scrollWidth - 4);
  }

  useEffect(() => {
    medirTira();
    const tira = stripRef.current;
    if (!tira) return undefined;
    // Al cambiar el ancho de la ventana cambia cuántas caben: se vuelve a medir.
    const observador = new ResizeObserver(medirTira);
    observador.observe(tira);
    return () => observador.disconnect();
  }, [photos.length]);

  function desplazarTira(sentido: -1 | 1) {
    const tira = stripRef.current;
    if (!tira) return;
    // Casi una página entera, pero no toda: la última foto visible se queda de referencia.
    tira.scrollBy({ left: sentido * tira.clientWidth * 0.8, behavior: 'smooth' });
  }

  /* Si el arrastre acaba fuera de la galería no llega `dragleave`: se apaga desde la ventana. */
  useEffect(() => {
    const apagar = () => setSobreGaleria(false);
    window.addEventListener('dragend', apagar);
    window.addEventListener('drop', apagar);
    return () => {
      window.removeEventListener('dragend', apagar);
      window.removeEventListener('drop', apagar);
    };
  }, []);

  function traeFicheros(event: DragEvent): boolean {
    return Array.from(event.dataTransfer.types).includes('Files');
  }

  /** Suelta fotos en la galería: se filtran las que no son imagen y se suben las demás. */
  function soltarEnGaleria(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setSobreGaleria(false);
    // Una foto de la propia galería soltada en el hueco entre dos: es el final de un arrastre
    // para ordenar, no una subida fallida.
    if (dragIdRef.current) {
      dropPhoto(event);
      return;
    }
    if (!event.dataTransfer.types.includes('Files')) return;
    if (!canManage || busy) return;
    const imagenes = Array.from(event.dataTransfer.files).filter((file) =>
      /^image\//.test(file.type),
    );
    if (imagenes.length === 0) {
      toast.error('Aquí solo van fotos: JPEG, PNG, WebP, AVIF o HEIC.');
      return;
    }
    upload.mutate(imagenes);
  }

  function markBroken(id: string) {
    setBroken((current) => (current.has(id) ? current : new Set(current).add(id)));
  }

  function sourceOf(photo: PropertyPhotoDto): string | null {
    return photo.url && !broken.has(photo.id) ? photo.url : null;
  }

  /**
   * Devuelve la promesa del refresco A PROPOSITO: quien borra la espera antes de cerrar su
   * dialogo. Sin eso, el dialogo se va, la lista sigue un instante con la fila que acabas de
   * borrar y el usuario ve —brevemente— algo que ya no existe.
   */
  function refresh(): Promise<unknown> {
    // La miniatura del listado sale de la portada: sin esto se queda la anterior.
    void queryClient.invalidateQueries({ queryKey: ['properties'] });
    return queryClient.invalidateQueries({ queryKey: queryKeys.property(propertyId) });
  }

  const upload = useMutation({
    /**
     * Los ficheros se suben DE UNO EN UNO aunque el usuario elija varios. En paralelo, cada
     * uno reordena y renumera al llegar, y el `sortOrder` final depende de quién gane la
     * carrera: el orden que ves al terminar no sería el que elegiste al seleccionar.
     */
    mutationFn: async (files: File[]) => {
      let done = 0;
      for (const file of files) {
        await api.uploadPhoto(propertyId, file, (percent) => {
          setProgress(Math.round(((done + percent / 100) / files.length) * 100));
        });
        done += 1;
      }
      return files.length;
    },
    onSuccess: (count) => {
      refresh();
      toast.success(count === 1 ? 'Foto subida.' : `${count} fotos subidas.`);
    },
    onError: (error: unknown) => {
      // El servidor ya explica el límite de tamaño, el de número y el formato rechazado.
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido subir la foto.');
    },
    onSettled: () => setProgress(null),
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.updatePhoto(id, body),
    onSuccess: refresh,
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido guardar.'),
  });

  /** Guarda un orden nuevo y, si la primera foto ha cambiado, la marca como portada. */
  const reorder = useMutation({
    mutationFn: async ({ ids, coverId }: { ids: string[]; coverId: string | null }) => {
      await api.reorderPhotos(propertyId, ids);
      if (coverId) await api.updatePhoto(coverId, { isCover: true });
    },
    onSuccess: refresh,
    onError: (error: unknown) => {
      // Vuelve al orden que de verdad está guardado.
      flip.capture();
      setOrder(null);
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido reordenar.');
    },
  });

  // Cuando la API devuelve las fotos (el orden ya guardado, o cualquier otro cambio), manda
  // ella. Durante un arrastre no: el orden provisional es el que se está viendo.
  useEffect(() => {
    if (!dragIdRef.current) setOrder(null);
  }, [photos]);

  const remove = useMutation({
    mutationFn: (id: string) => api.deletePhoto(id),
    onSuccess: async () => {
      await refresh();
      toast.success('Foto borrada.');
      setDeleting(null);
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido borrar.');
      setDeleting(null);
    },
  });

  /** Flechas ‹ ›: intercambia una foto con su vecina. Animado igual que el arrastre. */
  function move(index: number, direction: -1 | 1) {
    const ids = list.map((photo) => photo.id);
    const target = index + direction;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    commit(ids);
  }

  /** Fija un orden en pantalla y lo guarda. La primera foto pasa a ser la portada. */
  function commit(ids: string[]) {
    flip.capture();
    setOrder(ids);
    const first = ids[0] ?? null;
    reorder.mutate({ ids, coverId: first && first !== apiCover?.id ? first : null });
  }

  function startDrag(event: DragEvent<HTMLElement>, photoId: string) {
    dropped.current = false;
    dragIdRef.current = photoId;
    event.dataTransfer.effectAllowed = 'move';
    // Firefox no inicia el arrastre si no hay datos en el portapapeles interno.
    event.dataTransfer.setData('text/plain', photoId);
    // Se arrastra la FOTO entera, no la pastilla de las rayas.
    const visual = event.currentTarget.parentElement;
    if (visual) event.dataTransfer.setDragImage(visual, 24, 24);
    /*
     * El estado se toca en el siguiente ciclo: cambiar el DOM dentro del propio `dragstart`
     * hace que Chrome cancele el arrastre nada más empezar.
     */
    const ids = list.map((photo) => photo.id);
    setTimeout(() => {
      setDragId(photoId);
      setOrder((current) => current ?? ids);
    }, 0);
  }

  /** Pasar por encima de otra foto: la arrastrada ocupa su sitio y las demás se apartan. */
  function dragOverPhoto(event: DragEvent<HTMLElement>, targetId: string) {
    const dragged = dragIdRef.current;
    if (!dragged) return;
    // Sin esto el navegador rechaza la soltada y el arrastre no hace nada.
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (targetId === dragged || performance.now() < lockUntil.current) return;
    const ids = list.map((photo) => photo.id);
    const from = ids.indexOf(dragged);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    /*
     * Se SACA y se INSERTA, no se intercambia: al llevar la quinta al primer puesto, la gente
     * espera que las demás corran una posición, no que la primera se vaya al quinto lugar.
     */
    ids.splice(from, 1);
    ids.splice(to, 0, dragged);
    flip.capture();
    setOrder(ids);
    lockUntil.current = performance.now() + 260;
  }

  /** Para la galería y la tira: soltar en un hueco entre fotos cuenta igual. */
  function allowDrop(event: DragEvent<HTMLElement>) {
    if (!dragIdRef.current) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }

  function dropPhoto(event: DragEvent<HTMLElement>) {
    if (!dragIdRef.current) return;
    event.preventDefault();
    // Que no llegue a la galería: allí se interpretaría como una subida de ficheros.
    event.stopPropagation();
    dropped.current = true;
    dragIdRef.current = null;
    setDragId(null);
    const ids = list.map((photo) => photo.id);
    if (ids.join() === savedIds.join()) {
      setOrder(null);
      return;
    }
    commit(ids);
  }

  /** Soltada fuera de las fotos (o con Esc): todo vuelve a su sitio, también deslizándose. */
  function endDrag() {
    dragIdRef.current = null;
    setDragId(null);
    if (!dropped.current) {
      flip.capture();
      setOrder(null);
    }
  }

  /** El asa de tres rayas, arriba a la derecha de cada foto. */
  function dragHandle(photo: PropertyPhotoDto, size: number) {
    if (!canManage || photos.length < 2) return null;
    return (
      <span
        className={styles.dragHandle}
        draggable={!busy}
        title="Arrastra para ordenar"
        aria-hidden="true"
        onDragStart={(event) => startDrag(event, photo.id)}
        onDragEnd={endDrag}
      >
        <Icon name="grip-lines" size={size} strokeWidth={2.4} />
      </span>
    );
  }

  /** Abre la vista ampliada en la foto indicada. */
  function openViewer(photo: PropertyPhotoDto) {
    const index = photos.findIndex((item) => item.id === photo.id);
    if (index >= 0) setViewing(index);
  }

  /** La portada según la API, y el orden guardado con ella delante. */
  const apiCover = photos.find((photo) => photo.isCover) ?? photos[0] ?? null;
  const saved = apiCover ? [apiCover, ...photos.filter((photo) => photo !== apiCover)] : [];
  const savedIds = saved.map((photo) => photo.id);
  const byId = new Map(photos.map((photo) => [photo.id, photo]));
  /** Lo que se pinta: el orden provisional si lo hay. Su primera foto es la portada. */
  const list: PropertyPhotoDto[] = order
    ? order.map((id) => byId.get(id)).filter((photo): photo is PropertyPhotoDto => Boolean(photo))
    : saved;
  const busy = upload.isPending || reorder.isPending || patch.isPending;

  return (
    <>
      {/*
        LA GALERÍA ES LA ZONA DE SUBIDA. No hay botón «Subir fotos» aparte: se arrastran las
        fotos encima, o se pulsa un hueco vacío. Es donde se mira, así que es donde se suelta.
        Durante la subida el progreso se pinta encima de la propia galería.
      */}
      <div
        className={[
          styles.gallery,
          canManage && sobreGaleria ? styles.galleryOver : '',
          progress !== null ? styles.galleryBusy : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onDragEnter={(event) => {
          if (canManage && traeFicheros(event)) setSobreGaleria(true);
        }}
        onDragOver={(event) => {
          allowDrop(event);
          if (!canManage || !traeFicheros(event)) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setSobreGaleria(false);
          }
        }}
        onDrop={soltarEnGaleria}
      >
        {progress !== null && (
          <div className={styles.galleryProgress} role="status" aria-live="polite">
            Subiendo… {progress}%
          </div>
        )}
        {canManage && sobreGaleria && progress === null && (
          <div className={styles.galleryDropHint} aria-hidden="true">
            <Icon name="upload" size={18} strokeWidth={2.2} />
            Suelta las fotos
          </div>
        )}
        {list.length === 0 &&
          (canManage ? (
            /* Sin fotos, el hueco de la portada ES el sitio para subirlas: se pulsa o se suelta. */
            <button
              type="button"
              className={`${styles.coverEmpty} ${styles.emptyAction}`}
              onClick={() => inputRef.current?.click()}
              disabled={busy}
            >
              <span className={styles.emptyHint}>
                <Icon name="upload" size={18} strokeWidth={2.2} />
                Arrastra fotos aquí o pulsa para elegirlas
              </span>
            </button>
          ) : (
            <div className={styles.coverEmpty}>foto principal del inmueble</div>
          ))}

        {/*
          Portada y dos miniaturas en UNA lista con la clave de cada foto: así, cuando una
          miniatura pasa a portada, React MUEVE su nodo en vez de destruirlo y crear otro, y la
          animación puede llevarla deslizándose desde su hueco al grande.
        */}
        {list.slice(0, 3).map((photo, slot) => {
          const isCover = slot === 0;
          return (
            <div
              key={photo.id}
              ref={flip.register(`g:${photo.id}`)}
              className={[isCover ? styles.coverSlot : styles.thumbSlot, dragId === photo.id ? styles.dragging : '']
                .filter(Boolean)
                .join(' ')}
              onDragOver={(event) => dragOverPhoto(event, photo.id)}
              onDrop={dropPhoto}
            >
              {sourceOf(photo) ? (
                /* Es un BOTÓN y no una imagen con `onClick`: así se llega con el tabulador y
                   se activa con Intro, que es lo que espera quien no usa ratón. */
                <button
                  type="button"
                  className={isCover ? styles.coverButton : styles.thumbButton}
                  onClick={() => openViewer(photo)}
                  aria-label={
                    isCover ? 'Ver la foto principal a tamaño completo' : `Ver la foto ${slot + 1} a tamaño completo`
                  }
                >
                  <img
                    className={isCover ? styles.cover : styles.thumb}
                    src={photo.url!}
                    onError={() => markBroken(photo.id)}
                    /**
                     * El pie es el texto alternativo. Cuando no lo hay se describe qué es la
                     * imagen en vez de dejar un `alt` vacío, que para un lector de pantalla
                     * equivale a que la foto no exista.
                     */
                    alt={photo.caption || (isCover ? 'Foto principal del inmueble' : 'Foto del inmueble')}
                    loading="lazy"
                  />
                  {isCover && (
                    <span className={styles.zoomHint} aria-hidden="true">
                      <Icon name="search" size={16} strokeWidth={2.2} />
                    </span>
                  )}
                </button>
              ) : (
                <div className={isCover ? styles.coverEmpty : styles.thumbEmpty}>
                  {isCover ? 'foto sin fichero en el almacén' : null}
                </div>
              )}
              {dragHandle(photo, 16)}
            </div>
          );
        })}

        {/* Se completa hasta dos miniaturas para no romper la rejilla del maquetado. */}
        {[1, 2]
          .filter((slot) => slot >= Math.max(list.length, 1))
          .map((slot) =>
            canManage ? (
              <button
                key={`hueco-${slot}`}
                type="button"
                className={`${styles.thumbEmpty} ${styles.emptyAction}`}
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                aria-label="Añadir fotos"
                title="Añadir fotos"
              >
                <span className={styles.emptyHint}>
                  <Icon name="plus" size={18} strokeWidth={2.4} />
                </span>
              </button>
            ) : (
              <div key={`hueco-${slot}`} className={styles.thumbEmpty} />
            ),
          )}
      </div>

      {canManage && (
        <div className={styles.manager}>
          <div className={styles.managerHead}>
            <span className={styles.managerTitle}>
              {photos.length} foto{photos.length === 1 ? '' : 's'}
            </span>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              multiple
              className={styles.fileInput}
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                if (files.length > 0) upload.mutate(files);
                // Se limpia para que elegir OTRA VEZ el mismo fichero vuelva a disparar el
                // evento: sin esto, reintentar tras un error no hace nada.
                event.target.value = '';
              }}
            />
          </div>

          <p className={styles.managerHint}>
            Arrastra las fotos a la galería o pulsa un hueco. JPEG, PNG, WebP, AVIF o HEIC, hasta
            15 MB y 50 fotos por inmueble. Al subirlas se les quitan los metadatos EXIF, incluida
            la posición GPS.
          </p>

          {photos.length > 0 && (
            <div className={styles.stripWrap}>
              {haciaAtras && (
                <button
                  type="button"
                  className={`${styles.stripArrow} ${styles.stripArrowPrev}`}
                  onClick={() => desplazarTira(-1)}
                  aria-label="Fotos anteriores"
                >
                  <Icon name="chevron-left" size={18} strokeWidth={2.4} />
                </button>
              )}
              {haciaDelante && (
                <button
                  type="button"
                  className={`${styles.stripArrow} ${styles.stripArrowNext}`}
                  onClick={() => desplazarTira(1)}
                  aria-label="Fotos siguientes"
                >
                  <Icon name="chevron-right" size={18} strokeWidth={2.4} />
                </button>
              )}
            <ul
              className={styles.strip}
              ref={stripRef}
              onScroll={medirTira}
              onDragOver={allowDrop}
              onDrop={dropPhoto}
            >
              {list.map((photo, index) => (
                <li
                  key={photo.id}
                  ref={flip.register(`s:${photo.id}`)}
                  className={
                    dragId === photo.id ? `${styles.stripItem} ${styles.dragging}` : styles.stripItem
                  }
                  onDragOver={(event) => dragOverPhoto(event, photo.id)}
                  onDrop={dropPhoto}
                >
                  <div className={styles.stripVisual}>
                    {sourceOf(photo) ? (
                      <img
                        src={photo.url!}
                        onError={() => markBroken(photo.id)}
                        alt={photo.caption || `Foto ${index + 1}`}
                        loading="lazy"
                      />
                    ) : (
                      <span className={styles.stripMissing}>sin fichero</span>
                    )}
                    {index === 0 && <span className={styles.coverTag}>Portada</span>}
                    {/*
                      El asa de tres rayas es lo que se arrastra, no la tarjeta entera: así
                      seleccionar el texto del pie de foto ya no arrastra la foto sin querer, y
                      se ve de un vistazo que las fotos se pueden ordenar.
                    */}
                    {dragHandle(photo, 15)}
                    {/*
                      Borrar va SOBRE la foto y solo aparece al pasar el ratón: es la acción que
                      menos se usa y la única que no tiene vuelta atrás, así que no merece un
                      botón fijo al lado de las de ordenar, donde se pulsaba sin querer.
                      Con el teclado aparece al llegar a ella (`:focus-within`) y en pantallas
                      táctiles, que no tienen «encima», se ve siempre.
                    */}
                    <button
                      type="button"
                      className={styles.photoDelete}
                      onClick={() => setDeleting(photo)}
                      disabled={busy}
                      title="Borrar foto"
                      aria-label={`Borrar la foto ${index + 1}`}
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </div>

                  <div className={styles.stripActions}>
                    <button
                      type="button"
                      className={
                        index === 0 ? `${styles.iconButton} ${styles.iconButtonStar}` : styles.iconButton
                      }
                      // Se lleva al primer puesto, que ES ser portada: así se desliza como al arrastrar.
                      onClick={() =>
                        commit([photo.id, ...list.filter((other) => other.id !== photo.id).map((other) => other.id)])
                      }
                      disabled={index === 0 || busy}
                      title={index === 0 ? 'Es la foto principal' : 'Hacer foto principal'}
                      aria-label={index === 0 ? `La foto ${index + 1} es la principal` : `Hacer principal la foto ${index + 1}`}
                    >
                      {/* Estrella y no chincheta: la chincheta se leía como «ubicación». */}
                      <Icon name="star" size={14} />
                    </button>
                    <button
                      type="button"
                      className={styles.iconButton}
                      onClick={() => move(index, -1)}
                      disabled={index === 0 || busy}
                      aria-label={`Mover la foto ${index + 1} hacia delante`}
                    >
                      <Icon name="chevron-left" size={13} strokeWidth={2.4} />
                    </button>
                    <button
                      type="button"
                      className={styles.iconButton}
                      onClick={() => move(index, 1)}
                      disabled={index === list.length - 1 || busy}
                      aria-label={`Mover la foto ${index + 1} hacia atrás`}
                    >
                      <Icon name="chevron-right" size={13} strokeWidth={2.4} />
                    </button>
                  </div>

                  <input
                    className={styles.captionInput}
                    defaultValue={photo.caption ?? ''}
                    placeholder="Pie de foto"
                    aria-label={`Pie de la foto ${index + 1}`}
                    maxLength={200}
                    /**
                     * Se guarda al salir del campo y solo si ha cambiado: guardar en cada tecla
                     * serían veinte peticiones por frase, y un botón «guardar» por foto llenaría
                     * la tira de botones que casi nunca se pulsan.
                     */
                    onBlur={(event) => {
                      const value = event.target.value.trim();
                      if (value === (photo.caption ?? '')) return;
                      patch.mutate({ id: photo.id, body: { caption: value || null } });
                    }}
                  />
                </li>
              ))}
              {/*
                La baldosa «+» al final de la tira. Cuando la galería ya tiene sus tres huecos
                llenos no queda ningún sitio donde PULSAR para añadir más —solo arrastrar—, y
                arrastrar no existe en una tableta. No es un botón aparte: es una foto más que
                todavía no está.
              */}
              <li className={styles.stripItem}>
                <button
                  type="button"
                  className={`${styles.stripVisual} ${styles.stripAdd}`}
                  onClick={() => inputRef.current?.click()}
                  disabled={busy}
                  aria-label="Añadir fotos"
                  title="Añadir fotos"
                >
                  <Icon name="plus" size={20} strokeWidth={2.4} />
                </button>
              </li>
            </ul>
            </div>
          )}
        </div>
      )}

      {viewing !== null && (
        <Lightbox
          images={photos.map((photo) => ({
            id: photo.id,
            url: sourceOf(photo),
            caption: photo.caption ?? null,
            placeholderColor: photo.placeholderColor ?? null,
          }))}
          startIndex={viewing}
          onClose={() => setViewing(null)}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Borrar la foto"
          body={
            deleting.isCover
              ? 'Es la portada: al borrarla, la siguiente foto pasa a serlo. No se puede deshacer.'
              : 'Se quita de la ficha y del listado. No se puede deshacer.'
          }
          confirmLabel="Borrar"
          tone="danger"
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await remove.mutateAsync(deleting.id);
          }}
        />
      )}
    </>
  );
}
